import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { useTheme } from '@theme/useTheme';
import { trackGtmButtonClick } from '@/analytics/gtm';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const { handleOAuthCallback } = useAuth();
  const { colors } = useTheme();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing authentication...');

  useEffect(() => {
    const processOAuthCallback = async () => {
      try {
        setStatus('loading');
        setMessage('Processing Google authentication...');

        const result = await handleOAuthCallback();

        if (result.success && result.user) {
          setStatus('success');
          setMessage('Authentication successful! Redirecting...');

          // Wait a moment to show success message, then redirect
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 1500);
        } else {
          setStatus('error');
          setMessage(result.message || 'Authentication failed. Please try again.');

          // Redirect to home after showing error
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 3000);
        }
      } catch (error: any) {
        console.error('OAuth callback error:', error);
        setStatus('error');
        setMessage(error.message || 'Authentication failed. Please try again.');

        // Redirect to home after showing error
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 3000);
      }
    };

    processOAuthCallback();
  }, [handleOAuthCallback, navigate]);

  const getStatusIcon = () => {
    switch (status) {
      case 'loading':
        return (
          <div className="w-16 h-16 border-4 rounded-full animate-spin mx-auto mb-4 border-blue-500 border-t-transparent"></div>
        );
      case 'success':
        return (
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: colors.success + '20' }}>
            <div className="text-3xl" style={{ color: colors.success }}>✓</div>
          </div>
        );
      case 'error':
        return (
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: colors.error + '20' }}>
            <div className="text-3xl" style={{ color: colors.error }}>✗</div>
          </div>
        );
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'loading':
        return colors.primary;
      case 'success':
        return colors.success;
      case 'error':
        return colors.error;
      default:
        return colors.text;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
      <div className="max-w-md w-full mx-4">
        <div
          className="bg-white rounded-2xl shadow-xl p-8 text-center"
          style={{ backgroundColor: colors.background }}
        >
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img
              src="/logo.png"
              alt="Sleep Apnea Assessment Logo"
              className="h-16 w-auto object-contain"
            />
          </div>

          {/* Status Icon */}
          {getStatusIcon()}

          {/* Title */}
          <h1
            className="text-2xl font-bold mb-4"
            style={{ color: colors.heading }}
          >
            {status === 'loading' && 'Authenticating...'}
            {status === 'success' && 'Welcome!'}
            {status === 'error' && 'Authentication Failed'}
          </h1>

          {/* Message */}
          <p
            className="text-lg mb-6"
            style={{ color: getStatusColor() }}
          >
            {message}
          </p>

          {/* Additional info for error state */}
          {status === 'error' && (
            <div className="mt-4">
              <p className="text-sm" style={{ color: colors.text }}>
                You will be redirected to the home page shortly.
              </p>
              <button
                onClick={() => {
                  trackGtmButtonClick({
                    button_id: 'auth_callback_go_home',
                    section: 'page_auth_callback',
                    label: 'Go Home Now',
                    destination: '/',
                  });
                  navigate('/', { replace: true });
                }}
                className="mt-4 px-6 py-2 rounded-lg font-medium transition-colors duration-200"
                style={{
                  backgroundColor: colors.primary,
                  color: 'white'
                }}
              >
                Go Home Now
              </button>
            </div>
          )}

          {/* Loading dots for loading state */}
          {status === 'loading' && (
            <div className="flex justify-center space-x-1 mt-4">
              <div
                className="w-2 h-2 rounded-full animate-bounce"
                style={{ backgroundColor: colors.primary, animationDelay: '0ms' }}
              ></div>
              <div
                className="w-2 h-2 rounded-full animate-bounce"
                style={{ backgroundColor: colors.primary, animationDelay: '150ms' }}
              ></div>
              <div
                className="w-2 h-2 rounded-full animate-bounce"
                style={{ backgroundColor: colors.primary, animationDelay: '300ms' }}
              ></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthCallback;